import type { Id } from "@convex/_generated/dataModel";
import { Link2, Link2Off, Monitor, Plus, Wifi, WifiOff } from "lucide-react";

import type { StationType } from "~/components/stations/station-edit-types";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "~/components/ui/select";
import { toConvexTableId } from "~/lib/convex-table-id";

interface PairedDevicesSectionProps {
  station: StationType;
  unpairedDevices: {
    _id: string;
    name: string;
    active: boolean;
    stationId?: string | null;
  }[];
  allDevices: { _id: string }[] | undefined;
  pairDeviceMutation: {
    mutate: (data: {
      stationId: Id<"stations">;
      kdsDeviceId: Id<"kdsDevices">;
    }) => void;
  };
  unpairDeviceMutation: {
    mutate: (data: { kdsDeviceId: Id<"kdsDevices"> }) => void;
    isPending: boolean;
  };
}

export function PairedDevicesSection({
  station,
  unpairedDevices,
  allDevices,
  pairDeviceMutation,
  unpairDeviceMutation,
}: PairedDevicesSectionProps) {
  return (
    <div className="border-border space-y-3 rounded-xl border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="text-muted-foreground h-4 w-4" />
          <span className="font-medium">Paired Devices</span>
        </div>
        {unpairedDevices.length > 0 && (
          <Select
            onValueChange={(deviceId) => {
              if (deviceId) {
                pairDeviceMutation.mutate({
                  stationId: station._id,
                  kdsDeviceId: toConvexTableId("kdsDevices", deviceId),
                });
              }
            }}
            value=""
          >
            <SelectTrigger className="w-auto gap-2">
              <Plus className="h-4 w-4" />
              <span>Add Device</span>
            </SelectTrigger>
            <SelectContent>
              {unpairedDevices.map((device) => (
                <SelectItem key={device._id} value={device._id}>
                  <div className="flex items-center gap-2">
                    {device.active ? (
                      <Wifi className="text-success h-3 w-3" />
                    ) : (
                      <WifiOff className="text-muted-foreground h-3 w-3" />
                    )}
                    <span>{device.name}</span>
                    {device.stationId && (
                      <span className="text-muted-foreground text-xs">
                        (paired elsewhere)
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {station.pairedDevices.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <Monitor className="text-muted-foreground/50 h-8 w-8" />
          <p className="text-muted-foreground text-sm">
            No devices paired to this station
          </p>
          <p className="text-muted-foreground text-xs">
            Register a device from the KDS app, then pair it here
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {station.pairedDevices.map((device) => (
            <PairedDeviceItem
              device={device}
              key={device._id}
              onUnpair={() =>
                unpairDeviceMutation.mutate({
                  kdsDeviceId: toConvexTableId("kdsDevices", device._id),
                })
              }
              unpairPending={unpairDeviceMutation.isPending}
            />
          ))}
        </div>
      )}

      {allDevices && allDevices.length === 0 && (
        <p className="text-muted-foreground text-center text-xs">
          No KDS devices registered yet. Open the KDS app on a tablet to
          register.
        </p>
      )}
    </div>
  );
}

function PairedDeviceItem({
  device,
  onUnpair,
  unpairPending,
}: {
  device: { _id: string; name: string; active: boolean };
  onUnpair: () => void;
  unpairPending: boolean;
}) {
  return (
    <div className="bg-accent flex items-center justify-between rounded-lg px-3 py-2">
      <div className="flex items-center gap-3">
        <Monitor className="h-4 w-4" />
        <div>
          <p className="text-sm font-medium">{device.name}</p>
          <div className="flex items-center gap-1">
            {device.active ? (
              <>
                <Wifi className="text-success h-3 w-3" />
                <span className="text-success text-xs">Online</span>
              </>
            ) : (
              <>
                <WifiOff className="text-muted-foreground h-3 w-3" />
                <span className="text-muted-foreground text-xs">Offline</span>
              </>
            )}
          </div>
        </div>
      </div>
      <Button
        disabled={unpairPending}
        onClick={onUnpair}
        size="sm"
        variant="ghost"
      >
        <Link2Off className="text-destructive h-4 w-4" />
      </Button>
    </div>
  );
}
